import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import videojs from "video.js";
import { useStoreActions, useStoreState } from "easy-peasy";

const BGMPlayer = ({ source, type: audioType }) => {
  const bgmPlayerRef = useRef(null);
  const { setBgmPlayer, setPlaying } = useStoreActions(
    (actions) => actions.bgm
  );
  const { player: bgmPlayer, isPlaying } = useStoreState((state) => state.bgm);
  const { isMuted } = useStoreState((state) => state.experience);

  const handleCanPlay = useCallback(() => {
    if (bgmPlayer.readyState() <= 2) {
      bgmPlayer.load();
    }
  }, [bgmPlayer]);

  const handlePlaying = useCallback(() => {
    if (!isPlaying) {
      setPlaying(true);
    }
  }, [setPlaying, isPlaying]);

  const cleanUp = useCallback(() => {
    if (bgmPlayer) {
      setBgmPlayer(null);
    }
  }, [bgmPlayer, setBgmPlayer]);

  const onVisibilityChange = useCallback(() => {
    if (bgmPlayer && isPlaying) {
      if (document.visibilityState === "hidden") {
        bgmPlayer.pause();
      } else {
        bgmPlayer.play();
      }
    }
  }, [bgmPlayer, isPlaying]);

  useEffect(() => {
    if (source && audioType) {
      setBgmPlayer(
        videojs(bgmPlayerRef.current, {
          sources: [
            {
              src: source,
              type: audioType,
            },
          ],
        })
      );
    }
    return () => {};
  }, [source, setBgmPlayer, audioType]);

  useEffect(() => {
    if (bgmPlayer) {
      if (isPlaying) bgmPlayer.play();
      else {
        bgmPlayer.pause();
      }
      if (isMuted) bgmPlayer.muted(true);
      else {
        bgmPlayer.muted(false);
      }
    }
    return () => {};
  }, [bgmPlayer, isPlaying, isMuted]);

  useEffect(() => {
    if (bgmPlayer) {
      bgmPlayer.on("playing", handlePlaying);
    }
    return () => {};
  }, [bgmPlayer, handleCanPlay, handlePlaying]);

  useEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (bgmPlayer) {
      if (isPlaying) bgmPlayer.play();
      else {
        bgmPlayer.pause();
      }
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [bgmPlayer, isPlaying, onVisibilityChange]);

  useEffect(() => {
    return () => {
      cleanUp();
    };
  }, [cleanUp]);

  // TODO: Support captions for accesibility
  return (
    <div className="bg-player visually-hidden">
      <audio
        ref={bgmPlayerRef}
        className="video-js js-bg-player__player vjs-default-skin"
        preload="true"
        width="600"
        height="600"
        playsInline
        loop
        data-src={source}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
};

BGMPlayer.propTypes = {
  source: PropTypes.string,
  type: PropTypes.string,
};

BGMPlayer.defaultProps = {
  source: "",
  type: "",
};

export default BGMPlayer;
